import { catalogApi, patchResource, type CatalogRow } from './collections';
import { apiFetch } from './http';

/**
 * The promise made about a ticket (07 §4.7, 12 §7.3). The console never computes any of this: the
 * deadline, the state and the consumed fraction come from the server, and the browser only
 * interpolates the passing of time between answers. It never turns a met clock into a breached one
 * on its own - a client clock running fast must not be able to paint a breach that did not happen.
 */

export const SLA_CLOCK_STATES = ['running', 'paused', 'met', 'breached', 'cancelled'] as const;

export type SlaClockState = (typeof SLA_CLOCK_STATES)[number];

export interface SlaClockDetail {
  id: string;
  targetType: string;
  cycleNumber: number;
  state: SlaClockState;
  startedAt: string;
  dueAt: string;
  timeZone: string;
  durationMinutes: number;
  elapsedBusinessMinutes: number;
  remainingMinutes: number;
  consumedFraction: number;
  atRiskThresholdFraction: number;
  isAtRisk: boolean;
  pausedAt?: string | null;
  stoppedAt?: string | null;
  breachedAt?: string | null;
}

export const slaApi = {
  clocksOf: async (ticketId: string, signal?: AbortSignal): Promise<SlaClockDetail[]> =>
    (
      await apiFetch<{ items: SlaClockDetail[] }>(
        `/api/v1/tickets/${ticketId}/sla-clocks`,
        signal === undefined ? {} : { signal },
      )
    ).items,
};

/**
 * The five visual states of 13 §4.3.4, decided by the server's answer alone. "At risk" is a shade of
 * running and not a state of its own, which is why the row carries the flag instead of a sixth value.
 */
export type SlaVisualState = 'onTrack' | 'atRisk' | 'paused' | 'met' | 'breached';

export function visualStateOf(clock: SlaClockDetail): SlaVisualState | null {
  switch (clock.state) {
    case 'breached':
      return 'breached';
    case 'met':
      return 'met';
    case 'paused':
      return 'paused';
    case 'running':
      return clock.isAtRisk ? 'atRisk' : 'onTrack';
    default:
      // A cancelled clock made no promise anybody can miss, so it is not shown.
      return null;
  }
}

export const SLA_COLORS: Record<SlaVisualState, string> = {
  onTrack: 'steel',
  atRisk: 'yellow',
  paused: 'gray',
  met: 'green',
  breached: 'red',
};

/**
 * The same five states as they travel on the wire in the ticket summary of `GET /tickets`
 * (`AM-135` point 3): snake_case there, camelCase here. The list is also the whitelist of the
 * `slaState` filter - a value outside it answers `400 unsupported_filter`.
 */
export const SLA_SUMMARY_STATES = ['on_track', 'at_risk', 'paused', 'met', 'breached'] as const;

export type SlaSummaryState = (typeof SLA_SUMMARY_STATES)[number];

const SUMMARY_VISUAL_STATES: Record<SlaSummaryState, SlaVisualState> = {
  on_track: 'onTrack',
  at_risk: 'atRisk',
  paused: 'paused',
  met: 'met',
  breached: 'breached',
};

/**
 * The single crossing between the wire vocabulary and the visual one, so the colour table and the
 * `state.*` translation keys are written once and read from both the detail panel and the grid.
 */
export function visualStateOfSummary(state: SlaSummaryState): SlaVisualState {
  return SUMMARY_VISUAL_STATES[state];
}

/**
 * `/sla-policies` and their targets (07 §4.12, `admin.sla.manage`, `AM-134` point 11):
 * bounded collections, like the thirteen catalogs of the service catalog (`AM-115` point 5) - the
 * list call answers everything, without a cursor. `/sla-pause-reasons` is the matrix
 * `status_reason × target_type`, replaced whole (SLA-03), edited in the card of the SLA policies.
 */

export const SLA_TARGET_TYPES = [
  'first_response',
  'acknowledgement',
  'resolution',
  'restoration',
] as const;
export type SlaTargetType = (typeof SLA_TARGET_TYPES)[number];

/**
 * An SLA policy, full detail (07 §4.12): `/admin/sla-policies` (R-10) manages it under
 * `admin.sla.manage`. A contract only names one and shows the one it carries, which reads the
 * lighter `slaPolicyDirectoryQueryOptions` instead (revisión del manual, ronda 2, punto 2).
 */
export interface SlaPolicyDetail extends CatalogRow {
  name: string;
  description: string | null;
  organizationId: string | null;
  /**
   * `AM-375`: the name the organization reads with now, active or not, deleted or not, on every
   * answer about a policy; null when the policy names none.
   */
  organizationName: string | null;
  contractId: string | null;
  serviceId: string | null;
  /** `AM-210`: the rubro whose promise this is; it names no organization, service or contract. */
  serviceAreaId?: string | null;
  businessHoursId: string;
  priorityScope: string[] | null;
  appliesToTicketTypes: string[];
  breachWhilePaused: boolean;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SlaTargetDetail extends CatalogRow {
  slaPolicyId: string;
  targetType: string;
  priorityId: string;
  durationMinutes: number;
  atRiskThresholdFraction: number;
  usesBusinessHours: boolean;
  createdAt: string;
  updatedAt: string;
}

/** One cell of the pause matrix: the row *is* the mapping (SLA-03). */
export interface SlaPauseReasonEntry {
  statusReasonId: string;
  targetType: string;
}

export const slaPoliciesApi = catalogApi<SlaPolicyDetail>('/api/v1/sla-policies');

export const slaTargetsApi = {
  list: (policyId: string): Promise<SlaTargetDetail[]> =>
    apiFetch<{ items: SlaTargetDetail[] }>(`/api/v1/sla-policies/${policyId}/targets`).then(
      (page) => page.items,
    ),
  create: (policyId: string, body: Record<string, unknown>): Promise<SlaTargetDetail> =>
    apiFetch<SlaTargetDetail>(`/api/v1/sla-policies/${policyId}/targets`, {
      method: 'POST',
      body,
    }),
  update: (
    policyId: string,
    targetId: string,
    body: Record<string, unknown>,
    etag: string,
  ): Promise<SlaTargetDetail> =>
    patchResource<SlaTargetDetail>(
      `/api/v1/sla-policies/${policyId}/targets/${targetId}`,
      body,
      etag,
    ),
  remove: (policyId: string, targetId: string): Promise<void> =>
    apiFetch<void>(`/api/v1/sla-policies/${policyId}/targets/${targetId}`, { method: 'DELETE' }),
};

export const slaPauseReasonsApi = {
  /** `GET /sla-pause-reasons`: the whole matrix of "motivos de pausa" (R-10). */
  list: (): Promise<SlaPauseReasonEntry[]> =>
    apiFetch<{ items: SlaPauseReasonEntry[] }>('/api/v1/sla-pause-reasons').then(
      (page) => page.items,
    ),
  /** `PUT /sla-pause-reasons`: replaces the whole matrix; a pair the body does not carry stops pausing. */
  replace: (items: SlaPauseReasonEntry[]): Promise<SlaPauseReasonEntry[]> =>
    apiFetch<{ items: SlaPauseReasonEntry[] }>('/api/v1/sla-pause-reasons', {
      method: 'PUT',
      body: { items },
    }).then((page) => page.items),
};
